import * as React from "react";
import * as Actions from "../actions";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { StudyCoughEventType } from "webportalclient";
import { AnalyseState, PlayStateEnum } from "../interfaces/state";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { PermissionEnum } from "../../../misc/api/ApiPermissionEnum";
import { useSecurityContext } from "../../../Core/SecurityContext";
import { EventTypeSelector } from "./EventTypeSelector";
import { TimeInput } from "./EventAddInputs";
import { CommitSource, FigureCommitEvent, FigureOptions, FigureType, PeaksInstance, Point, PointMouseEvent, PointOptions, Segment, SegmentDragEvent, SegmentOptions, WaveformViewMouseEvent } from "peaks.js";
import { GlobalHotKeys } from "react-hotkeys";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faLocationPin, faRotateLeft, faTrash } from "@fortawesome/free-solid-svg-icons";
import { KeyboardShortcutTooltip } from "../../../misc/components/KeyboardShortcutTooltip";
import { OnIgnoreEnter } from "../AnalyseApp";
import { throttle } from "lodash";

export interface EventActionDashboardProps {
  sessionId: string;
  isViewOnly: boolean;
  areKeyboardShortcutsVisible: boolean;
  availableEventTypes: StudyCoughEventType[];
  selectedEventType: StudyCoughEventType;
  selectedWaveform: string;
  isPlaying: boolean;
  peaksJs: PeaksInstance;
  currentWaveformDuration: number;
  isAcceptabilityModalDisplayed: boolean;
  primaryAnalysisSetId: string;
  positionsLoaded: boolean;
  selectedSegment: number;
  canAddEventByDoubleClick: boolean;
  dispatch: Dispatch<AnalyseState>;
}

export const EventActionDashboard = ({
  sessionId, isViewOnly, areKeyboardShortcutsVisible,
  availableEventTypes, selectedEventType, selectedWaveform,
  isPlaying, peaksJs, currentWaveformDuration, isAcceptabilityModalDisplayed,
  primaryAnalysisSetId, positionsLoaded, selectedSegment, canAddEventByDoubleClick,
  dispatch
}: EventActionDashboardProps) => {
  const client = useWebPortalApiClient();
  const legacyClient = useWebPortalLegacyClient();
  const { currentUserPermissions } = useSecurityContext();
  const [hasAddEventPermissionOnAllWaveforms, setHasAddEventPermissionOnAllWaveforms] = React.useState(false);
  const [isQCWaveform, setIsQCWaveform] = React.useState<boolean>();
  const [startTime, setStartTime] = React.useState<number>(0);
  const [isValidStartTime, setIsValidStartTime] = React.useState(true);
  const [endTime, setEndTime] = React.useState<number>(0);
  const [isValidEndTime, setIsValidEndTime] = React.useState(true);

  const [hasFigureSelected, setHasFigureSelected] = React.useState(false);
  const [hasFigureModified, setHasFigureModified] = React.useState(false);

  const [startTimeInput, setStartTimeInput] = React.useState(null);
  const [endTimeInput, setEndTimeInput] = React.useState(null);

  const minTime = (selectedSegment - 1) * 24 * 60 * 60;
  const maxTime = minTime + currentWaveformDuration - 0.001;

  const onFigureSelected = (figure: FigureOptions) => {
    setHasFigureSelected(true);
  };

  const onFigureDeselected = (figure: FigureOptions) => {
    setHasFigureSelected(false);
  };

  const onFigureSnapshotted = (figure: FigureOptions) => {
    setHasFigureModified(true);
    setIsValidStartTime(true);

    if (figure.figureType == FigureType.Point) {
      setStartTime((figure as Point).time);
    } else {
      setStartTime((figure as Segment).startTime)
      setEndTime((figure as Segment).endTime)
      setIsValidEndTime(true);
    }

    dispatch(Actions.ChangeSelectedEventAction.create(availableEventTypes.find(e => e.id == figure.eventTypeId)));
  };

  const onFigureCommit = (event: FigureCommitEvent) => {
    if (event.source == CommitSource.Current) {
      dispatch(Actions.MoveEventAction.create(event.figure, event.discard, legacyClient))
    }

    setHasFigureModified(false);
  };

  const onPointDragEnd = (event: PointMouseEvent) => {
    setStartTime(event.point.time);
  };

  const onSegmentDragEnd = (event: SegmentDragEvent) => {
    setStartTime(event.segment.startTime);
    setEndTime(event.segment.endTime);
  };

  const onViewDblClick = (viewName: string, event: WaveformViewMouseEvent) => {
    if (peaksJs.editing || !canAddEventByDoubleClick) {
      return;
    }

    return dispatch(Actions.AddEventAction.create({
      id: sessionId,
      startTime: event.time,
      isPoint: true
    }, legacyClient));
  };

  const onOverviewDblClick = (event: WaveformViewMouseEvent) => {
    return onViewDblClick('overview', event);
  };

  const onZoomviewDblClick = (event: WaveformViewMouseEvent) => {
    return onViewDblClick('zoomview', event);
  };

  React.useEffect(() => {
    const checkQCWaveform = async () => {
      if (currentUserPermissions.some(x => (x.Id === PermissionEnum.AddEventOnAnyWaveform))) {
        setHasAddEventPermissionOnAllWaveforms(true);
      }

      const selectedSessionWaveform = (await client.client.getCoughAnalysisSet(primaryAnalysisSetId))?.sessionWaveform;

      setIsQCWaveform(selectedWaveform === selectedSessionWaveform);

      dispatch(
        Actions.UpdateAddEventByDoubleClickStateAction.create(
          (selectedWaveform === selectedSessionWaveform) || hasAddEventPermissionOnAllWaveforms
        )
      );
    }
    checkQCWaveform();
  }, [selectedWaveform, currentUserPermissions, hasAddEventPermissionOnAllWaveforms, dispatch]);

  React.useEffect(() => {
    if (isViewOnly) return;

    const updateStartTime = throttle(newTime => {
      if (!peaksJs.editing) {
          setStartTime(newTime + peaksJs.options.multiDayOffset);
      }
    }, 500);

    peaksJs.on('figure.selected', onFigureSelected);
    peaksJs.on('figure.deselected', onFigureDeselected);
    peaksJs.on('figure.snapshotted', onFigureSnapshotted);
    peaksJs.on('figure.commit', onFigureCommit);

    peaksJs.on("points.dragend", onPointDragEnd);
    peaksJs.on("segments.dragend", onSegmentDragEnd);

    peaksJs.on('overview.dblclick', onOverviewDblClick);
    peaksJs.on('zoomview.dblclick', onZoomviewDblClick);

    peaksJs.on("player.timeupdate", updateStartTime);

    return () => {
      peaksJs.off('figure.selected', onFigureSelected);
      peaksJs.off('figure.deselected', onFigureDeselected);
      peaksJs.off('figure.snapshotted', onFigureSnapshotted);
      peaksJs.off('figure.commit', onFigureCommit);

      peaksJs.off("points.dragend", onPointDragEnd);
      peaksJs.off("segments.dragend", onSegmentDragEnd);

      peaksJs.off('overview.dblclick', onOverviewDblClick);
      peaksJs.off('zoomview.dblclick', onZoomviewDblClick);

      peaksJs.off("player.timeupdate", updateStartTime);
    };
  }, [peaksJs]);


  const onStartTimeBlur = () => {
    peaksJs.player.seek(startTime - peaksJs.options.multiDayOffset);
  }

  const onStartTimeChange = (timeInSeconds: number, target: HTMLInputElement & EventTarget) => {
    setIsValidStartTime(true);

    if (target.validity.patternMismatch || !target.validity.valid || isNaN(timeInSeconds)) {
      setIsValidStartTime(false);
      return;
    }

    setStartTime(timeInSeconds);

    if (!peaksJs.editing) {
      return;
    }

    const updates = selectedEventType?.period? {startTime: timeInSeconds} as Partial<SegmentOptions>: {time: timeInSeconds} as Partial<PointOptions>;
    peaksJs.figures.update(peaksJs.figures.selected, updates);
  };

  const onEndTimeChange = (timeInSeconds: number, target: HTMLInputElement & EventTarget) => {
    setIsValidEndTime(true);

    if (!target.value || !selectedEventType?.period) {
      setIsValidEndTime(true);
      setEndTime(undefined);
      return;
    }

    if (target.validity.patternMismatch || !target.validity.valid || isNaN(timeInSeconds)) {
      setIsValidEndTime(false);
      return;
    }

    setEndTime(timeInSeconds);

    if (!peaksJs.editing) {
      return;
    }

    (peaksJs.figures.selected as Segment).update({endTime: timeInSeconds});
  };

  function AddOrConfirmButton() {
    if (isViewOnly) {
      return <></>;
    }

    const [isAdd] = React.useState(hasFigureModified == false);
    const [isDisabled] = React.useState(!isValidStartTime || !isValidEndTime || (selectedEventType.period && endTime <= startTime) ||
                                        !positionsLoaded || !!isAcceptabilityModalDisplayed ||
                                        (!isQCWaveform && !hasAddEventPermissionOnAllWaveforms)
                                       );

    const onAddFigure = function(e) {
      if (e) {
        e.target.blur();
      }

      const startTimeComputed = peaksJs.time.getCurrentTime();

      if (selectedEventType.period) {
        var endTimeComputed = (endTime ?? peaksJs.options.multiDayOffset) - peaksJs.options.multiDayOffset;
        if (endTimeComputed === 0 || (e as KeyboardEvent)?.key !== undefined) { // Events via 'enter' need an endTime adding
          endTimeComputed = startTimeComputed + 3;

          // if the computed endTime is at 24:00:00 or 48:00:00 ... etc
          // this is not allowed, add one more second to signal the outside range error 
          if (endTimeComputed % (24 * 60 * 60) == 0) {
              ++endTimeComputed;
          }
        }

        dispatch(Actions.AddEventAction.create({
          id: sessionId,
          startTime: startTimeComputed,
          endTime: endTimeComputed,
          isPoint: false
        }, legacyClient));
      }
      else {
        dispatch(Actions.AddEventAction.create({
          id: sessionId,
          startTime: startTimeComputed,
          isPoint: true
        }, legacyClient));
      }
    };

    const onConfirmFigure = function(e) {
      if (e) {
        e.target.blur();
      }

      (peaksJs.figures.selected as any).commit(CommitSource.Current);
      setIsValidStartTime(true);
      setIsValidEndTime(true);
    };

    const keyMap = {
      addEvent: 'enter',
    }

    const handlers = {
      'addEvent': (e) => {
        if (!isPlaying && !isDisabled) {
          onAddFigure(e);
        }
      }
    }

    return (
    <>
      <GlobalHotKeys keyMap={keyMap} handlers={handlers} allowChanges />
      <button id="AddEvent" className={`btn ${isAdd? 'btn-primary': 'btn-success'}`} type="button" disabled={isDisabled || isPlaying}
              onClick={e => isAdd? onAddFigure(e): onConfirmFigure(e) } onKeyDown={OnIgnoreEnter}>
        <FontAwesomeIcon icon={isAdd? faLocationPin: faCheck} className="fa-fw" aria-hidden="true" />&nbsp;
        {isAdd? 'Add Event': 'Confirm'}
        &nbsp;
        <KeyboardShortcutTooltip key="KeyTooltip" show={areKeyboardShortcutsVisible} keyName="ENTER" />
      </button>
    </>
    );
  }

  function CancelButton() {
    if (isViewOnly || !hasFigureModified) {
      return <></>;
    }

    const onCancelChanges =() => {
      const selected = peaksJs.figures.selected as any;
      selected.commit(CommitSource.Snapshot);
    };

    const keyMap = {
      cancelEvent: 'esc'
    }

    const handlers = {
      'cancelEvent': () => onCancelChanges(),
    }

    return (
      <>
        &nbsp;
        <GlobalHotKeys keyMap={keyMap} handlers={handlers} />
        <button id="CancelEvent" className="btn btn-info" type="button" onClick={() => onCancelChanges()} onKeyDown={OnIgnoreEnter}>
            <FontAwesomeIcon icon={faRotateLeft} className="fa-fw" aria-hidden="true" />&nbsp;
            Cancel
            &nbsp;
            <KeyboardShortcutTooltip key="KeyTooltip" show={areKeyboardShortcutsVisible} keyName="ESC" />
        </button>
      </>
    );
  }

  function DeleteButton() {
    if (hasFigureSelected == false || isViewOnly) {
      return <></>;
    }

    const onDeleteFigure =() => {
      const actions = {
        [FigureType.Point]: Actions.RemovePointEventAction.create,
        [FigureType.Segment]: Actions.RemovePeriodEventAction.create
      };

      dispatch(actions[peaksJs.figures.selected.figureType](legacyClient));
      setIsValidStartTime(true);
      setIsValidEndTime(true);
    };

    const keyMap = {
      deleteEvent: 'delete',
    }

    const handlers = {
      'deleteEvent': () => onDeleteFigure(),
    }

    return (
      <>
        &nbsp;
        <GlobalHotKeys keyMap={keyMap} handlers={handlers} />
        <button id="DeleteEvent" className="btn btn-danger" type="button" onClick={() => onDeleteFigure()} onKeyDown={OnIgnoreEnter}>
            <FontAwesomeIcon icon={faTrash} className="fa-fw" aria-hidden="true" />&nbsp;
            Delete
            &nbsp;
            <KeyboardShortcutTooltip key="KeyTooltip" show={areKeyboardShortcutsVisible} keyName="DELETE" />
        </button>
      </>
    );
  }

  return (
    <div className="col-lg-5">
      <div className="col-lg-4 col-md-12" style={{display: isViewOnly? 'none': 'block'}}>
          <EventTypeSelector
            isVisible={!isViewOnly}
            events={availableEventTypes}
            eventSelected={selectedEventType}
            isEditing={hasFigureModified}
            onChange={event => {
              if (startTimeInput != null) {
                startTimeInput.value = '';
                onStartTimeChange(0, startTimeInput);
              }

              if (endTimeInput != null) {
                endTimeInput.value = '';
                onEndTimeChange(0, endTimeInput);
              }

              dispatch(Actions.ChangeSelectedEventAction.create(event));
            }
          }
          />&nbsp;
      </div>
      <div className="col-lg-4 col-md-12">
        <TimeInput id="startTimeInput"
          labelText={selectedEventType?.period? "Start": "Position"}
          minTimeValue={minTime}
          maxTimeValue={maxTime}
          value={startTime}
          isEditMode={!isPlaying}
          isValid={isValidStartTime}
          onChange={onStartTimeChange}
          onBlur={onStartTimeBlur}
          onInputReady={input => setStartTimeInput(input)}
        />
        <TimeInput id="endTimeInput"
          labelText="End"
          minTimeValue={minTime}
          maxTimeValue={maxTime}
          value={endTime}
          isEditMode={!isPlaying}
          isValid={isValidEndTime}
          isVisible={selectedEventType?.period}
          onChange={onEndTimeChange}
          onInputReady={input => setEndTimeInput(input)}
        />
      </div>
      <div className="col-lg-4 col-md-12">
        <AddOrConfirmButton />
        <CancelButton />
        <DeleteButton />
      </div>
    </div>
  );
}

function mapStateToProps(state: AnalyseState) {
  return {
    sessionId: state.core.sessionMetadata.sessionId,
    isViewOnly: state.core.isViewOnly,
    areKeyboardShortcutsVisible: state.core.areKeyboardShortcutsVisible,
    availableEventTypes: state.core.availableEventTypes,
    selectedEventType: state.core.selectedEventType,
    selectedWaveform: state.core.selectedWaveform,
    isPlaying: state.core.playState === PlayStateEnum.Playing,
    peaksJs: state.core.peaksJs,
    currentWaveformDuration: state.core.audioLength,
    isAcceptabilityModalDisplayed: state.core.isAcceptabilityAuditModalVisible,
    primaryAnalysisSetId: state.analysisSet.primaryAnalystSetId,
    positionsLoaded: state.core.positions ? true : false,
    selectedSegment: state.core.selectedSegment,
    canAddEventByDoubleClick: state.core.canAddEventByDoubleClick
  } as EventActionDashboardProps;
}

export default connect<EventActionDashboardProps, any>(mapStateToProps)(EventActionDashboard);
